import { useTranslation } from 'react-i18next';
import { Heading, Flex, Spinner, Tooltip, Button } from '@radix-ui/themes';

import SectionCard from '../../../../shared/SectionCard/SectionCard.jsx';
import { useReportsBroadcastsSummary } from '../data.js';
import styles from './BroadcastsSummary.module.css';

const MiniBar = ({ value, max = 100, color = 'var(--violet-9)' }) => (
  <div className={styles.miniBar}>
    <div
      className={styles.miniBarFill}
      style={{ width: `${(value / max) * 100}%`, background: color }}
    />
  </div>
);

const MetricCard = ({
  value,
  label,
  suffix = '',
  icon,
  accent = false,
  tooltip,
}) => {
  const card = (
    <div
      className={`${styles.metricCard} ${
        accent ? styles.metricCardAccent : ''
      }`}
    >
      {icon && (
        <div className={styles.metricIcon}>
          <i className={icon} />
        </div>
      )}
      <div className={styles.metricContent}>
        <span className={styles.metricValue}>
          {value}
          {suffix && <span className={styles.metricSuffix}>{suffix}</span>}
        </span>
        <span className={styles.metricLabel}>{label}</span>
      </div>
    </div>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{card}</Tooltip>;
  }

  return card;
};

export const BroadcastsSummary = ({ startDate, endDate, onViewAll }) => {
  const { t } = useTranslation('reports');
  const { data, isLoading } = useReportsBroadcastsSummary({
    startDate,
    endDate,
  });

  if (isLoading) {
    return (
      <SectionCard
        headerContent={
          <Flex align="center" gap="2">
            <i
              className="fa-regular fa-paper-plane"
              style={{ color: 'var(--violet-9)' }}
            />
            <Heading size="4" m="0">
              {t('summary.broadcasts.title')}
            </Heading>
          </Flex>
        }
      >
        <Flex align="center" justify="center" py="6">
          <Spinner size="3" />
        </Flex>
      </SectionCard>
    );
  }

  const totalMessages = data.emailsSent + data.smsSent;
  const emailPercent =
    totalMessages > 0 ? Math.round((data.emailsSent / totalMessages) * 100) : 0;
  const smsPercent = 100 - emailPercent;

  return (
    <SectionCard
      headerContent={
        <Flex align="center" justify="between" width="100%">
          <Flex align="center" gap="2">
            <i
              className="fa-regular fa-paper-plane"
              style={{ color: 'var(--violet-9)' }}
            />
            <Heading size="4" m="0">
              {t('summary.broadcasts.title')}
            </Heading>
          </Flex>
          {onViewAll && (
            <Button variant="ghost" size="1" onClick={onViewAll}>
              View All <i className="fa-regular fa-arrow-right" />
            </Button>
          )}
        </Flex>
      }
    >
      {/* Top: Key metrics row */}
      <div className={styles.metricsRow}>
        <MetricCard
          value={data.totalSent}
          label={t('summary.broadcasts.totalSent')}
          icon="fa-regular fa-paper-plane"
          accent
        />
        <MetricCard
          value={data.audienceReached.toLocaleString()}
          label={t('summary.broadcasts.audienceReached')}
          icon="fa-regular fa-users"
        />
        <MetricCard
          value={data.uniqueOpens.toLocaleString()}
          label={t('summary.broadcasts.uniqueOpens')}
          icon="fa-regular fa-envelope-open"
        />
        <MetricCard
          value={
            data.linkTrackingDisabled ? '-' : data.linkClicks.toLocaleString()
          }
          label={t('summary.broadcasts.linkClicks')}
          icon="fa-regular fa-link"
          tooltip={
            data.linkTrackingDisabled
              ? t('broadcasts.linkTrackingDisabled')
              : undefined
          }
        />
      </div>

      {/* Bottom: Channel split + Recent broadcasts */}
      <div className={styles.bottomRow}>
        {/* Channel breakdown */}
        <div className={styles.channelSection}>
          <p className={styles.sectionLabel}>
            {t('summary.broadcasts.channelBreakdown')}
          </p>
          <div className={styles.channelBars}>
            <div className={styles.channelItem}>
              <div className={styles.channelInfo}>
                <i className="fa-regular fa-envelope" />
                <span className={styles.channelName}>
                  {t('summary.broadcasts.emailsSent')}
                </span>
                <span className={styles.channelCount}>
                  {data.emailsSent.toLocaleString()}
                </span>
                <span className={styles.channelPercent}>{emailPercent}%</span>
              </div>
              <MiniBar value={emailPercent} color="var(--violet-9)" />
            </div>
            <div className={styles.channelItem}>
              <div className={styles.channelInfo}>
                <i className="fa-regular fa-message-sms" />
                <span className={styles.channelName}>
                  {t('summary.broadcasts.smsSent')}
                </span>
                <span className={styles.channelCount}>
                  {data.smsSent.toLocaleString()}
                </span>
                <span className={styles.channelPercent}>{smsPercent}%</span>
              </div>
              <MiniBar value={smsPercent} color="var(--violet-6)" />
            </div>
          </div>
        </div>

        {/* Recent broadcasts */}
        <div className={styles.recentSection}>
          <p className={styles.sectionLabel}>
            {t('summary.broadcasts.recentBroadcasts')}
          </p>
          <div className={styles.recentList}>
            {data.recentBroadcasts.map((broadcast, index) => (
              <div key={index} className={styles.recentItem}>
                <div className={styles.recentContent}>
                  <span className={styles.recentSubject}>
                    {broadcast.subject}
                  </span>
                  <span className={styles.recentMeta}>
                    {broadcast.sentAt} &middot; {broadcast.recipients}{' '}
                    recipients
                  </span>
                </div>
                <div className={styles.recentStats}>
                  {broadcast.channel !== 'sms' && (
                    <span className={styles.recentStat}>
                      <i className="fa-regular fa-envelope-open" />
                      {broadcast.uniqueOpens.toLocaleString()}
                    </span>
                  )}
                  <span className={styles.recentStat}>
                    <i className="fa-regular fa-link" />
                    {data.linkTrackingDisabled
                      ? '-'
                      : broadcast.linkClicks.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
